/**
 * Light zones and the grade of the lens (lifted from the shared prototype
 * engine's `engine/src/light-zones.js` `sampleZones` and the dungeon-floors
 * prototype's `dungeon-zones.js`). Each zone nudges temperature, light and air
 * in a small world-space texture over the floor's own rectangle (passed in, no
 * global), which the lens samples per pixel from depth; its alpha is the lamp
 * warmth near fixtures. The theme's `grade` sets the lens's grade uniforms;
 * the edge-haze colours mean what a black pixel finally shows, so they are
 * inverted through the output tone map (ACES filmic at the tier's exposure).
 */
import type { Theme } from '@ab/content-schema';
import type { Floor } from '@ab/game-core';
import * as THREE from 'three';
import { presentation } from './constants.ts';
import type { LensUniforms } from './lens.ts';
import type { LightingPlan, Zone } from './lighting-plan.ts';

const smooth = (e0: number, e1: number, v: number): number => {
  const t = Math.min(1, Math.max(0, (v - e0) / (e1 - e0)));
  return t * t * (3 - 2 * t);
};

const weight = (zone: Zone, x: number, z: number): number => {
  if (zone.circle !== undefined) {
    const [cx, cz, r] = zone.circle;
    return 1 - smooth(r, r + zone.feather, Math.hypot(x - cx, z - cz));
  }
  if (zone.rect === undefined) return 0;
  const [x0, z0, x1, z1] = zone.rect;
  return 1 - smooth(0, zone.feather, Math.hypot(Math.max(x0 - x, 0, x - x1), Math.max(z0 - z, 0, z - z1)));
};

/** Zone channels at a world point; later zones override earlier ones. */
const sampleZones = (x: number, z: number, out: THREE.Vector3, zones: readonly Zone[]): THREE.Vector3 => {
  out.set(0, 0, 0);
  for (const zone of zones) {
    const w = weight(zone, x, z);
    if (w <= 0) continue;
    out.x += (zone.temp - out.x) * w;
    out.y += (zone.light - out.y) * w;
    out.z += (zone.air - out.z) * w;
  }
  return out;
};

/** The zone map over the floor (two texels per tile), with lamp warmth in alpha. */
export const bakeZoneMap = (floor: Floor, plan: LightingPlan, theme: Theme): { texture: THREE.DataTexture; rect: readonly [number, number, number, number] } => {
  const z = presentation.medium.zoneMap;
  const w = Math.min(z.maxSize, floor.width * z.texelsPerTile);
  const h = Math.min(z.maxSize, floor.height * z.texelsPerTile);
  const data = new Uint8Array(w * h * 4);
  const rect = [-0.5, -floor.height + 0.5, floor.width, floor.height] as const;
  const lampR2 = (theme.grade.lampRadiusPercent / 100) * (theme.grade.lampRadiusPercent / 100);
  const lamps = [...plan.torches, ...plan.lanterns];
  const v = new THREE.Vector3();
  for (let y = 0; y < h; y += 1) {
    for (let x = 0; x < w; x += 1) {
      const px = rect[0] + ((x + 0.5) / w) * rect[2];
      const pz = rect[1] + ((y + 0.5) / h) * rect[3];
      sampleZones(px, pz, v, plan.zones);
      const i = (y * w + x) * 4;
      data[i] = Math.min(255, Math.max(0, Math.round(128 + v.x * 255)));
      data[i + 1] = Math.min(255, Math.max(0, Math.round(128 + v.y * 255)));
      data[i + 2] = Math.min(255, Math.max(0, Math.round(128 + v.z * 255)));
      let warmth = 0;
      for (const t of lamps) warmth = Math.max(warmth, Math.exp(-((px - t.x) * (px - t.x) + (pz - t.z) * (pz - t.z)) / lampR2));
      data[i + 3] = Math.round(warmth * 255);
    }
  }
  const texture = new THREE.DataTexture(data, w, h);
  texture.minFilter = THREE.LinearFilter;
  texture.magFilter = THREE.LinearFilter;
  texture.needsUpdate = true;
  return { texture, rect };
};

// ACES filmic as three's GLSL chunk (column-major matrices), for inverting the haze colours.
const acesIn = [
  [0.59719, 0.35458, 0.04823],
  [0.076, 0.90834, 0.01566],
  [0.0284, 0.13383, 0.83777],
] as const;
const acesOut = [
  [1.60475, -0.53108, -0.07367],
  [-0.10208, 1.10813, -0.00605],
  [-0.00327, -0.07276, 1.07602],
] as const;
type Rgb = readonly [number, number, number];
const mul = (m: typeof acesIn | typeof acesOut, v: Rgb): Rgb => [m[0][0] * v[0] + m[0][1] * v[1] + m[0][2] * v[2], m[1][0] * v[0] + m[1][1] * v[1] + m[1][2] * v[2], m[2][0] * v[0] + m[2][1] * v[1] + m[2][2] * v[2]];
const fit = (x: number): number => (x * (x + 0.0245786) - 0.000090537) / (x * (0.983729 * x + 0.432951) + 0.238081);
const acesForward = (rgb: Rgb, exposure: number): Rgb => {
  const [r, g, b] = mul(acesOut, mul(acesIn, [(rgb[0] * exposure) / 0.6, (rgb[1] * exposure) / 0.6, (rgb[2] * exposure) / 0.6]).map(fit) as unknown as Rgb);
  return [Math.min(1, Math.max(0, r)), Math.min(1, Math.max(0, g)), Math.min(1, Math.max(0, b))];
};
/** The linear colour that the output tone map turns into `target` (fixed-point iteration, as the prototype). */
const acesInverse = (target: Rgb, exposure: number): Rgb => {
  let x: [number, number, number] = [Math.max(1e-5, target[0]), Math.max(1e-5, target[1]), Math.max(1e-5, target[2])];
  for (let i = 0; i < presentation.medium.lens.acesSteps; i += 1) {
    const y = acesForward(x, exposure);
    x = [0, 1, 2].map((c) => Math.max(0, (x[c] ?? 0) * Math.min(4, Math.max(0.25, ((target[c] ?? 0) + 1e-5) / ((y[c] ?? 0) + 1e-5))))) as [number, number, number];
  }
  return x;
};

/** The theme's grade and the floor's zone map into the lens. */
export const applyGrade = (u: LensUniforms, theme: Theme, zoneMap: THREE.Texture, rect: readonly [number, number, number, number]): void => {
  const g = theme.grade;
  const pct = (v: number): number => v / 100;
  u.zoneMap.value = zoneMap;
  u.zoneRect.value.set(...rect);
  u.zoneStrength.value = pct(g.zoneStrengthPercent);
  u.gradeShadow.value.set(...(g.shadowPercent.map(pct) as unknown as Rgb));
  u.gradeHighlight.value.set(...(g.highlightPercent.map(pct) as unknown as Rgb));
  u.gradeGain.value.set(...(g.gainPercent.map(pct) as unknown as Rgb));
  u.gradeContrast.value = pct(g.contrastPercent);
  u.gradeSaturation.value = pct(g.saturationPercent);
  u.gradeVignette.value.setScalar(pct(g.vignettePercent));
  u.hazeAmount.value = 0;
  u.shadowLift.value.set(...(g.liftPermille.map((v) => v / 1000) as unknown as Rgb));
  u.night.value = pct(g.lampWarmthPercent);
  u.focusBand.value.y = pct(g.tiltPercent);
  u.grain.value = pct(g.grainPercent);
  u.dust.value = pct(g.dustPercent);
  const exposure = presentation.medium.exposure;
  const haze = (hex: string): Rgb => {
    const c = new THREE.Color(hex);
    return acesInverse([c.r, c.g, c.b], exposure);
  };
  u.edgeHazeTop.value.set(...haze(g.hazeTop));
  u.edgeHazeBottom.value.set(...haze(g.hazeBottom));
  u.edgeHaze.value.set(pct(g.hazeClearPercent), pct(g.hazeMaxPercent), 0, 0);
  u.tiltScale.value.set(pct(g.tiltTopPercent), pct(g.tiltBottomPercent));
  u.tiltRamp.value.z = pct(g.tiltBottomStartPercent);
  u.tiltRamp.value.w = pct(g.tiltBottomEndPercent);
};

// ---------------------------------------------------------------- a scene's (slice 6a)

/** A scene's zone channels at a world point (three.js x and z), and its lamp warmth: the town's mood follows the traveler's. */
export const zoneAt = (x: number, z: number, out: THREE.Vector3, zones: readonly Zone[]): THREE.Vector3 => sampleZones(x, z, out, zones);

/**
 * A scene's zone map (the engine's `light-zones.js` `bakeZoneMap`): its zones
 * over `rect` (x0, z0, width, depth in three.js axes) at `texelsPerUnit`,
 * each channel 0.5 + the offset, and the lamps' proximity in alpha (the lens
 * warms what stands near a lantern at night).
 */
export const bakeSceneZones = (zones: readonly Zone[], rect: readonly [number, number, number, number], texelsPerUnit: number, lamps: readonly { readonly x: number; readonly z: number }[]): THREE.DataTexture => {
  const width = Math.round(rect[2] * texelsPerUnit);
  const height = Math.round(rect[3] * texelsPerUnit);
  const data = new Uint8Array(width * height * 4);
  const v = new THREE.Vector3();
  const unit = (c: number): number => Math.round(Math.min(1, Math.max(0, 0.5 + c)) * 255);
  for (let j = 0; j < height; j += 1) {
    for (let i = 0; i < width; i += 1) {
      const px = rect[0] + (i + 0.5) / texelsPerUnit;
      const pz = rect[1] + (j + 0.5) / texelsPerUnit;
      sampleZones(px, pz, v, zones);
      const k = (j * width + i) * 4;
      data[k] = unit(v.x);
      data[k + 1] = unit(v.y);
      data[k + 2] = unit(v.z);
      let lamp = 0;
      for (const l of lamps) lamp = Math.max(lamp, Math.exp(-((px - l.x) ** 2 + (pz - l.z) ** 2) / 1.6));
      data[k + 3] = Math.round(lamp * 255);
    }
  }
  const texture = new THREE.DataTexture(data, width, height);
  texture.magFilter = THREE.LinearFilter;
  texture.minFilter = THREE.LinearFilter;
  texture.needsUpdate = true;
  return texture;
};

// A scene's grade is its time of day's (`scene/look.ts` `applyLookGrade`, ADR 0033).
